import dedent from 'ts-dedent'
import {
  ExtensionComponentDefinition,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentInput,
  WorkflowComponentMetadata,
  WorkflowComponentOutput,
  WorkflowComponentOutputType,
  WorkflowComponentParamType
} from '../../types'
import { WorkflowComponent } from './workflowcomponent'

// A workflow component that is part of an extension
export class ExtensionWorkflowComponent extends WorkflowComponent {
  version = '1'
  componentDefinition: ExtensionComponentDefinition

  constructor(componentDefinition: ExtensionComponentDefinition) {
    for (const input of componentDefinition.inputs) {
      const paramType =
        WorkflowComponentParamType[
          Object.entries(WorkflowComponentParamType).find(
            ([key, val]) => val === input.type
          )?.[0] as string
        ]
      if (!paramType) {
        throw new Error(`Wrong parameter type: ${input.type}`)
      }
    }
    super(
      {
        name: componentDefinition.name,
        group: componentDefinition.group ?? 'Extension components',
        title: componentDefinition.title ?? componentDefinition.name,
        description:
          componentDefinition.description ?? componentDefinition.name,
        icon: componentDefinition.icon ?? undefined
      } as WorkflowComponentMetadata,
      componentDefinition.inputs as WorkflowComponentInput[],
      componentDefinition.outputs as WorkflowComponentOutput[]
    )
    this.version = (componentDefinition.version as string) ?? '1'
    this.componentDefinition = componentDefinition
  }

  getVersion(provider: Provider): string {
    return this.version
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTables: Record<string, string> = {}
    for (const output of this.outputs) {
      const tablename = connection.getOutputTableName(
        workflow.id,
        componentName,
        params,
        output.name,
        codeType
      )
      outputTables[output.name] = tablename
    }
    const preparedParams = this._prepareParameters(params)

    const inputs: string[] = this.componentDefinition.inputs.map((x) => {
      if (workflow.isExpression(componentName, x.name)) {
        return (preparedParams[x.name] as string) ?? 'null'
      } else {
        if (
          [
            WorkflowComponentParamType.String,
            WorkflowComponentParamType.GeoJsonDraw,
            WorkflowComponentParamType.GeoJson
          ].includes(x.type)
        ) {
          return preparedParams[x.name]
            ? connection.quoteMultilineString(preparedParams[x.name] as string)
            : 'null'
        } else if (
          [
            WorkflowComponentParamType.InputTable,
            WorkflowComponentParamType.OutputTable,
            WorkflowComponentParamType.Selection,
            WorkflowComponentParamType.Column,
            WorkflowComponentOutputType.Table
          ].includes(x.type)
        ) {
          return preparedParams[x.name] ? `'${preparedParams[x.name]}'` : 'null'
        } else {
          return (preparedParams[x.name] as string) ?? 'null'
        }
      }
    })
    const outputs = this.componentDefinition.outputs.map((x) => {
      return outputTables[x.name] ? `'${outputTables[x.name]}'` : 'null'
    })
    const procedureParams = inputs
    procedureParams.push(...outputs)
    procedureParams.push(
      codeType === WorkflowCodeType.DryRun ? 'true' : 'false'
    )
    if (this.componentDefinition.cartoEnvVars) {
      const usedEnvVars = {}
      for (const envVar of this.componentDefinition.cartoEnvVars) {
        if (envVar == 'tempStoragePath') {
          usedEnvVars[envVar] = connection.tempLocation
        } else if (envVar == 'workspaceLocation') {
          usedEnvVars[envVar] = connection.workspaceLocation
        } else if (envVar == 'extensionsLocation') {
          usedEnvVars[envVar] = connection.extensionsLocation
        } else if (envVar == 'tempLocation') {
          usedEnvVars[envVar] = connection.tempLocation
        } else {
          if (Object.keys(envVars).includes(envVar)) {
            usedEnvVars[envVar] = envVars[envVar]
          }
        }
      }
      procedureParams.push(`'${JSON.stringify(usedEnvVars)}'`)
    }
    const paramsString = procedureParams.join(',\n')
    const procedureName = `${connection.quoteName(
      connection.extensionsLocation
    )}.${this.componentDefinition.procedureName}`
    let query = dedent`
            CALL ${procedureName}(
              ${paramsString}
            );`
    query = connection.wrapCodeInIfTableExistsBlock(
      query,
      Object.values(outputTables),
      codeType
    )
    return {
      query: query,
      outputs: outputTables
    }
  }
}
